import {
  AfterViewInit,
  ApplicationRef,
  Directive,
  ElementRef,
  HostListener,
  inject,
  NgZone,
  OnDestroy,
  Optional,
  PLATFORM_ID,
  Self,
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { NgControl } from '@angular/forms';

/**
 * Bridges browser autofill / password-manager fill into Angular reactive forms.
 *
 * Angular's DefaultValueAccessor only updates its FormControl when the native
 * `input` event fires. Chrome fires `input` on autofill, but Firefox and Safari
 * frequently populate the field's DOM value WITHOUT firing `input`/`change` and
 * without the `:-webkit-autofill` animation (Firefox especially, on page load).
 * The control then stays empty/pristine/invalid even though the field visibly
 * has text — which leaves Save/Next buttons DISABLED (their `[disabled]` reads
 * the form validity) and makes submit handlers early-return so the API is never
 * called. Because the button is disabled, the user cannot even click to force a
 * flush; only a full reload appears to "fix" it. The problem is amplified under
 * zoneless change detection (superadmin), where there is no zone.js safety net
 * to schedule a change-detection pass.
 *
 * This directive keeps the model in sync from three angles:
 *   1. Interactive events browsers DO emit — `change`, `blur`, and the
 *      `:-webkit-autofill` `animationstart` hook (Chrome/Safari).
 *   2. A short proactive poll after the view initialises, to catch on-load
 *      autofill that fires no event at all (the Firefox case). This is what lets
 *      a disabled button re-enable itself without a click or refresh.
 * When the DOM value has diverged from the control value it re-dispatches a real
 * `input` event: that single event drives the value accessor (control updates),
 * sibling `(input)` handlers (text-case/number sanitising), AND the zoneless
 * change-detection scheduler — so any button bound to the form re-evaluates.
 *
 * Apply it to a native `<input>`/`<textarea>` bound to a reactive form control,
 * e.g. `<input [formControl]="control" appAutofillSync />`. The
 * `:-webkit-autofill` keyframe hook must exist in the host component's styles
 * (see InputReusable) for the Chrome/Safari instant-fill path.
 */
@Directive({
  selector: '[appAutofillSync]',
  standalone: true,
  host: { 'data-autofill-sync': '' },
})
export class AutofillSyncDirective implements AfterViewInit, OnDestroy {
  // Milliseconds after view init at which we re-check for silent autofill.
  // Autofill on load lands within the first second or so; these self-terminate.
  private static readonly pollSchedule = [50, 150, 350, 700, 1200, 2000];

  private readonly platformId = inject(PLATFORM_ID);
  private readonly ngZone = inject(NgZone);
  private readonly appRef = inject(ApplicationRef);
  private readonly timers: ReturnType<typeof setTimeout>[] = [];
  private tickQueued = false;

  constructor(
    private readonly elementRef: ElementRef<HTMLInputElement | HTMLTextAreaElement>,
    @Optional() @Self() private readonly ngControl: NgControl | null,
  ) {}

  ngAfterViewInit(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    // Run the poll outside Angular so the timers themselves don't churn change
    // detection; reconcile() re-enters via the dispatched input event only when
    // it actually finds an out-of-sync (autofilled) value.
    this.ngZone.runOutsideAngular(() => {
      for (const delay of AutofillSyncDirective.pollSchedule) {
        this.timers.push(setTimeout(() => this.reconcile(), delay));
      }
    });
  }

  ngOnDestroy(): void {
    for (const timer of this.timers) {
      clearTimeout(timer);
    }
    this.timers.length = 0;
  }

  @HostListener('change')
  @HostListener('blur')
  onReconcile(): void {
    this.reconcile();
  }

  @HostListener('input')
  onInput(): void {
    // Under zoneless change detection a form input may fail to refresh derived
    // UI that lives in an ancestor component (e.g. a Save/Next button whose
    // [disabled] reads this form's validity via a @ViewChild). Force one
    // coalesced app-wide tick so those cross-component bindings re-evaluate.
    // No-op under zone.js, where the event already schedules change detection.
    this.scheduleAppTick();
  }

  private scheduleAppTick(): void {
    if (NgZone.isInAngularZone() || this.tickQueued) {
      return;
    }

    this.tickQueued = true;
    queueMicrotask(() => {
      this.tickQueued = false;
      try {
        this.appRef.tick();
      } catch {
        // A tick may already be running; ignore the re-entrancy guard.
      }
    });
  }

  @HostListener('animationstart', ['$event'])
  onAnimationStart(event: AnimationEvent): void {
    // Chrome/Safari run this keyframe the instant they apply :-webkit-autofill.
    if (event.animationName === 'onAutoFillStart') {
      this.reconcile();
    }
  }

  private reconcile(): void {
    const element = this.elementRef.nativeElement;

    if (element.readOnly || element.disabled) {
      return;
    }

    // Number inputs are not autofill targets and have their own blur/sanitise
    // pipeline in the host component — leave them untouched.
    if (element instanceof HTMLInputElement && element.type === 'number') {
      return;
    }

    // Only act when we can see the model has fallen out of sync with the DOM.
    // If there is no bound control we cannot compare safely, so do nothing.
    const control = this.ngControl?.control;
    if (!control) {
      return;
    }

    const domValue = element.value ?? '';
    const modelValue = control.value == null ? '' : String(control.value);
    if (domValue === modelValue) {
      return;
    }

    // Re-dispatch the event Angular's value accessor actually listens to. This
    // updates the control, runs sibling (input) handlers, and (in zoneless mode)
    // notifies the change-detection scheduler — exactly like a real keystroke.
    element.dispatchEvent(new Event('input', { bubbles: true }));
  }
}
